/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuFeedback } from '@neutron/react';

/**
 * Internal Imports
 */
import { Dropdown } from '../../components/shared/dropdowns';

import { RootState } from '../../redux/store';
import { useGetUnitsQuery } from '../../redux/apis/Config.api';
import {
  usePostAddFavoriteUnitMutation,
  usePostRemoveFavoriteUnitMutation,
  usePostUpdateCurrentFacilityMutation
} from '../../redux/apis/Users.api';

import { useDebounceValue } from '../../utils/debouncers';
import { compareObjByStringKey, toTitleCaseSpecial } from '../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { IFacility, IPrimaryUnit, Unit } from '../../config/interfaces';

/**
 * Style Imports
 */
import {
  FacilityInput,
  IconFavoriteUnitWithHover,
  IconFavoriteUnitSavedWithHover,
  InputHeading,
  LeftContainer,
  SpinnerContainer,
  UnitItem,
  UnitItemHeading,
  UnitLabel,
  UnitList,
  UnitListContainer,
  UnitListContainerLeft,
  UnitListContainerRight,
  WrapperContainer
} from './Account.styles';

interface IFacilityAndUnitSettingsProps {
  facilityId: string;
  userId: string;
  facilities: IFacility[];
  primaryUnit: IPrimaryUnit[];
}

const FacilityAndUnitSettings: FC<IFacilityAndUnitSettingsProps> = ({
  facilityId,
  userId,
  facilities,
  primaryUnit
}) => {
  const [availableUnits, setAvailableUnits] = useState<Unit[]>([]);
  const [availablePrimaryUnits, setAvailablePrimaryUnits] =
    useState(primaryUnit);
  const [selectedFacility, setSelectedFacility] = useState(facilityId);
  const [isFacilityOpen, setIsFacilityOpen] = useState(false);
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [query, setQuery] = useState('');

  const debouncedQuery = useDebounceValue(query, 250);

  const [
    updateCurrentFacility
    // {
    //   isError: isUpdateCurrentFacilityError,
    //   isLoading: isUpdateCurrentFacilityLoading,
    //   isSuccess: isUpdateCurrentFacilitySuccess
    // }
  ] = usePostUpdateCurrentFacilityMutation();
  const [
    addFavoriteUnit,
    {
      //   isError: isAddFavoriteUnitError,
      isLoading: isAddFavoriteUnitLoading
      //   isSuccess: isAddFavoriteUnitSuccess
    }
  ] = usePostAddFavoriteUnitMutation();
  const [
    removeFavoriteUnit,
    {
      //   isError: isRemoveFavoriteUnitError,
      isLoading: isRemoveFavoriteUnitLoading
      //   isSuccess: isRemoveFavoriteUnitSuccess
    }
  ] = usePostRemoveFavoriteUnitMutation();
  const {
    data: unitsData
    // isError: isUnitsError,
    // isFetching: isUnitsFetching,
    // isLoading: isUnitsLoading
  } = useGetUnitsQuery(
    {
      facilityId,
      isUnfiltered: false,
      active: true
    },
    { skip: !facilityId }
  );

  const handleTypeAheadChange = (e: CustomEvent<any>) => {
    const target = e.target as HTMLInputElement;
    const searchInput = target?.value || '';
    setQuery(searchInput);
  };

  const markAsFavorite = async (item: IPrimaryUnit) => {
    await addFavoriteUnit({
      userId,
      unitId: item.unitId,
      facilityId: item.facilityId
    });
  };
  const removeFromFavorite = async (item: IPrimaryUnit) => {
    await removeFavoriteUnit({
      userId,
      unitId: item.unitId,
      facilityId: item.facilityId
    });
  };

  const setFacilityForUpdate = (selectedFac: IFacility) => {
    setSelectedFacility(selectedFac.facilityId);
    setActiveSearch('');
    setQuery('');
  };

  const facilityName = useMemo(() => {
    const foundFacility =
      facilities &&
      facilities.filter(
        (item: IFacility) => item.facilityId === selectedFacility
      );
    return foundFacility[0]?.value;
  }, [facilities, selectedFacility]);

  const facilitySearchResults = useMemo(() => {
    switch (activeSearch) {
      case 'facility':
        return facilities.filter((facility: IFacility) =>
          facility.value
            .toLowerCase()
            .includes(debouncedQuery.trim().toLowerCase())
        );
      default:
        return undefined;
    }
  }, [activeSearch, facilities, debouncedQuery]);

  useEffect(() => {
    if (unitsData && unitsData.length > 0 && primaryUnit) {
      const filteredPrimaryUnits = primaryUnit.filter(
        primary => primary.facilityId === facilityId
      );
      const filteredUnits = unitsData.filter(
        (item: Unit) =>
          !filteredPrimaryUnits.find(
            primary =>
              item.unitId === primary.unitId &&
              item.facilityId === primary.facilityId
          )
      );
      setAvailableUnits(filteredUnits);
      setAvailablePrimaryUnits(filteredPrimaryUnits);
    }
  }, [primaryUnit, unitsData]);

  useEffect(() => {
    if (selectedFacility && selectedFacility !== facilityId) {
      updateCurrentFacility({
        userId,
        facilityId: selectedFacility
      });
    }
  }, [selectedFacility]);

  return (
    <WrapperContainer id="Facility-Unit-Settings">
      <LeftContainer>
        <FacilityInput>
          <InputHeading>Select Current Facility</InputHeading>
          <Dropdown
            id="Facility-Selector"
            focused={activeSearch === 'facility'}
            name="facility"
            open={isFacilityOpen}
            placeholder={facilityName || ''}
            query={query}
            typeAhead
            setOpen={setIsFacilityOpen}
            setTypeAhead={setActiveSearch}
            setTypeAheadChange={handleTypeAheadChange}
          >
            {activeSearch === 'facility' &&
            facilitySearchResults &&
            facilitySearchResults.length > 0 ? (
              facilitySearchResults.map((item: IFacility) => (
                <div
                  key={item.id}
                  id={`Facility-List-Item-${item.id}`}
                  className={`dropdown-hover-item${
                    selectedFacility === item.facilityId ? ' selected' : ''
                  }`}
                  aria-selected={selectedFacility === item.facilityId}
                  role="option"
                  tabIndex={0}
                  onClick={() => setFacilityForUpdate(item)}
                  onKeyDown={() => setFacilityForUpdate(item)}
                >
                  {toTitleCaseSpecial(item.value)}
                </div>
              ))
            ) : (
              <div className="dropdown-search-no-result">No results found!</div>
            )}
          </Dropdown>
        </FacilityInput>
        <UnitListContainer>
          <UnitListContainerLeft>
            <UnitItemHeading>
              <UnitLabel>
                Unit List
                <SpinnerContainer>
                  {isAddFavoriteUnitLoading || isRemoveFavoriteUnitLoading ? (
                    <NeuFeedback
                      kind="spinner"
                      size="small"
                      style={{ height: '40px', width: '40px' }}
                    />
                  ) : (
                    ''
                  )}
                </SpinnerContainer>
              </UnitLabel>
            </UnitItemHeading>
            <UnitList className="neu-bg-plain-0" unit="group">
              {availableUnits &&
                [...availableUnits]
                  .sort(compareObjByStringKey('unit'))
                  .map(item => {
                    return (
                      <UnitItem
                        key={item.unitId}
                        interactive
                        unit="item"
                        style={{ '--list-padding': '0.65rem' }}
                      >
                        <UnitLabel key={item.unitId}>
                          {item.unit}
                          <IconFavoriteUnitWithHover
                            key={item.unitId}
                            fill={false}
                            onClick={() => markAsFavorite(item)}
                          >
                            star_border
                          </IconFavoriteUnitWithHover>
                        </UnitLabel>
                      </UnitItem>
                    );
                  })}
            </UnitList>
          </UnitListContainerLeft>
          <UnitListContainerRight>
            <UnitItemHeading>
              <UnitLabel>
                Favorite Units
                <SpinnerContainer>
                  {isAddFavoriteUnitLoading || isRemoveFavoriteUnitLoading ? (
                    <NeuFeedback
                      kind="spinner"
                      size="small"
                      style={{ height: '40px', width: '40px' }}
                    />
                  ) : (
                    ''
                  )}
                </SpinnerContainer>
              </UnitLabel>
            </UnitItemHeading>
            <UnitList className="neu-bg-plain-0" unit="group">
              {availablePrimaryUnits &&
                [...availablePrimaryUnits]
                  .sort(compareObjByStringKey('unit'))
                  .map(item => {
                    return (
                      <UnitItem
                        key={`${item.facilityId}:${item.unitId}`}
                        interactive
                        unit="item"
                        style={{ '--list-padding': '0.65rem' }}
                      >
                        <UnitLabel key={item.unitId}>
                          {item.unit}
                          <IconFavoriteUnitSavedWithHover
                            onClick={() => removeFromFavorite(item)}
                            key={item.unitId}
                          >
                            star
                          </IconFavoriteUnitSavedWithHover>
                        </UnitLabel>
                      </UnitItem>
                    );
                  })}
              {availablePrimaryUnits && availablePrimaryUnits.length === 0 && (
                <UnitItem key="no-primary-unit">
                  <UnitLabel key="no-primary-unit">No items</UnitLabel>
                </UnitItem>
              )}
            </UnitList>
          </UnitListContainerRight>
        </UnitListContainer>
      </LeftContainer>
    </WrapperContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    facilityId: state.AuthorizedUser.authorizedUser.facilityId,
    facilities: state.ConfigReducer.userAccessibleFacilities,
    primaryUnit: state.AuthorizedUser.authorizedUser.primaryUnit,
    userId: state.AuthorizedUser.authorizedUser.hcaid
  };
};

export default connect(mapReduxStateToProps)(FacilityAndUnitSettings);
